import { isRouteErrorResponse, Link as RouterLink, useRouteError } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';

import { DASHBOARD_PATH } from 'config';
import { buildLoginRedirectPath } from 'utils/route-guard/authRedirect';

export function getRouteErrorPresentation(error) {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        status: '404',
        title: 'This page does not exist',
        message: 'The link is outdated or the page was moved. Start from the dashboard or sign in again.'
      };
    }

    if (error.status === 401 || error.status === 403) {
      return {
        status: `${error.status}`,
        title: 'Your session is no longer active',
        message: 'Sign in again to continue where you left off.'
      };
    }

    if (error.status === 503) {
      return {
        status: '503',
        title: 'The service is temporarily unavailable',
        message: 'The app could not reach a required service. Reload the page in a moment.'
      };
    }

    return {
      status: `${error.status}`,
      title: 'This screen could not be loaded',
      message: 'Reload the page or return to the dashboard.'
    };
  }

  const rawMessage =
    (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message) || '';
  const looksAuthRelated = /auth|session|unauthor|forbidden/i.test(rawMessage);

  if (looksAuthRelated) {
    return {
      status: 'Session',
      title: 'Your session is no longer active',
      message: 'Sign in again to continue where you left off.'
    };
  }

  return {
    status: 'App Error',
    title: 'This screen hit an unexpected problem',
    message: 'Reload the page or sign in again. If it keeps happening, the page logic needs a fix.'
  };
}

export default function ErrorBoundary() {
  const error = useRouteError();
  const presentation = getRouteErrorPresentation(error);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const currentSearch = typeof window !== 'undefined' ? window.location.search : '';
  const loginHref = buildLoginRedirectPath(currentPath, currentSearch);
  const detailMessage =
    error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' ? error.message : '';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 3,
        py: 6,
        background:
          'radial-gradient(circle at top left, rgba(33, 150, 243, 0.18), transparent 35%), linear-gradient(180deg, #f8fbff 0%, #eef3f9 100%)'
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 720,
          borderRadius: 4,
          p: { xs: 3, md: 5 },
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.12)'
        }}
      >
        <Stack spacing={3}>
          <Chip label={presentation.status} color="primary" variant="outlined" sx={{ alignSelf: 'flex-start' }} />

          <Stack spacing={1}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
              {presentation.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
              {presentation.message}
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button component={RouterLink} to={loginHref} variant="contained" startIcon={<LoginRoundedIcon />}>
              Sign In Again
            </Button>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={() => window.location.reload()}>
              Reload Page
            </Button>
            <Button component={RouterLink} to={DASHBOARD_PATH} variant="text" startIcon={<SpaceDashboardRoundedIcon />}>
              Dashboard
            </Button>
          </Stack>

          {import.meta.env.DEV && detailMessage ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'grey.100',
                color: 'text.secondary',
                fontFamily: 'monospace'
              }}
            >
              {detailMessage}
            </Box>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
